/**
 * 認證錯誤處理工具
 */

export const AuthErrorHandler = {
  /**
   * 錯誤代碼對應的用戶友好訊息
   */
  errorMessages: {
    // LINE OAuth 錯誤
    'line_auth_failed': 'LINE 登入失敗，請重試',
    'no_code': 'LINE 授權失敗，未收到授權碼',
    'auth_failed': 'LINE 認證失敗，請檢查網路連接',
    'invalid_token': '無效的認證令牌',
    'user_creation_failed': '用戶建立失敗',
    
    // 會話錯誤
    'session_fetch_failed': '獲取登入資料失敗',
    'session_expired': '登入會話已過期',
    'no_data': '未收到有效的認證資料',
    
    // Token 錯誤
    'token_expired': '登入已過期，請重新登入',
    'token_invalid': '認證令牌無效',
    'refresh_failed': '刷新登入狀態失敗',
    
    // 網路錯誤
    'network_error': '網路連接失敗，請檢查網路設置',
    'server_error': '伺服器錯誤，請稍後再試',
    
    // 通用錯誤
    'unknown_error': '發生未知錯誤，請稍後再試'
  },

  /**
   * 獲取用戶友好的錯誤訊息
   */
  getErrorMessage: (errorCode) => {
    return AuthErrorHandler.errorMessages[errorCode] || AuthErrorHandler.errorMessages.unknown_error;
  },

  /**
   * 處理認證錯誤
   */
  handleAuthError: (error, navigate) => {
    let errorCode = 'unknown_error';
    let shouldRedirect = true;
    let redirectPath = '/login';

    // 根據錯誤類型決定處理方式
    if (error.response) {
      // HTTP 錯誤
      const status = error.response.status;
      
      switch (status) {
        case 401:
          errorCode = 'token_expired';
          break;
        case 403:
          errorCode = 'token_invalid';
          shouldRedirect = false; // 403 通常不需要重導向到登入頁
          break;
        case 404:
          errorCode = 'session_expired';
          break;
        case 500:
          errorCode = 'server_error';
          break;
        default:
          errorCode = 'unknown_error';
      }
    } else if (error.request) {
      // 網路錯誤
      errorCode = 'network_error';
    } else if (error.code) {
      // 自定義錯誤代碼
      errorCode = error.code;
    }

    const message = AuthErrorHandler.getErrorMessage(errorCode);
    
    // 記錄錯誤
    console.error('認證錯誤:', {
      code: errorCode,
      message,
      originalError: error
    });

    // 顯示錯誤訊息（可以整合 toast 或其他通知系統）
    if (window.showErrorToast) {
      window.showErrorToast(message);
    } else {
      console.warn('錯誤訊息:', message);
    }

    // 重導向處理
    if (shouldRedirect && navigate) {
      // 儲存當前路徑以便登入後返回
      if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/callback') {
        sessionStorage.setItem('auth_redirect', window.location.pathname + window.location.search);
      }
      
      navigate(`${redirectPath}?error=${errorCode}`);
    }

    return {
      code: errorCode,
      message,
      shouldRedirect
    };
  },

  /**
   * 檢查錯誤是否需要重新登入
   */
  requiresReauth: (error) => {
    const reAuthCodes = [
      'token_expired',
      'token_invalid',
      'session_expired',
      'line_auth_failed',
      'auth_failed'
    ];

    const errorCode = error.code || error.response?.status;
    return reAuthCodes.includes(errorCode) || errorCode === 401;
  },

  /**
   * 從 URL 參數獲取錯誤訊息
   */
  getErrorFromUrl: (searchParams) => {
    const errorCode = searchParams.get('error');
    if (!errorCode) return null;

    return {
      code: errorCode,
      message: AuthErrorHandler.getErrorMessage(errorCode)
    };
  }
};

export default AuthErrorHandler;